import React, { Fragment, useEffect, useState } from 'react';

import { Button } from '@mui/material';

import { Box, Tab, Tabs } from "@mui/material";
import SelectDateRange from '../../components/SelectDateRange';
import SelectMakes from '../../components/SelectMakes';
import SelectModels from '../../components/SelectModels';
import SelectYears from '../../components/SelectYears';
import APIService from '../../shared/api-service';
import ICampaign from '../../shared/ICampaign';
import IData from '../../shared/IData';
import IMake from '../../shared/IMake';
import IModel from '../../shared/IModel';
import IState from '../../shared/IState';
import BillingAddress from '../BillingAddress/BillingAddress';
import GeoFence from '../GeoFence/GeoFence';
import {
  ICampaignStatistics, IPlatformStatistics, IStatistics, IStatisticsNotification, IStatisticsReply
} from './CampaignStatistics';
import ViewTabSection from './ViewTabSection';

import Styles from '../../lib/Styles';
import classes from './View.module.scss';
import SelectVINs from '../../components/SelectVINs/SelectVINs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other} className='tabpanel'
    >
      {value === index && (
        <Box sx={{ p: 1 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

export interface IViewProps {
  setIsViewPaneOpen: Function;
  handleDuplicate: Function;
  data: IData | undefined;
}

enum StatusTypeId {
  iOS = 0,
  Android = 1
}

enum AppReplyTypeId {
  AppNotifyReceived = 0,
  AppNotifyRead = 1,
  AppNotifyDeleted = 2
}

export default function View(props: IViewProps) {
  const { setIsViewPaneOpen, handleDuplicate, data } = props;
  const cx = new Styles(classes).cx;

  const [selectedMakes, setSelectedMakes] = useState<Array<IMake>>([]);
  const [selectedModels, setSelectedModels] = useState<Array<IModel>>([]);
  const [selectedYears, setSelectedYears] = useState<Array<Number>>([]);
  const [selectedUSStates, setSelectedUSStates] = useState<Array<IState>>([]);
  const [selectedUSState, setSelectedUSState] = useState<IState>();
  const [selectedZipCodes, setSelectedZipCodes] = useState<string>();
  const [selectedDistance, setSelectedDistance] = useState<{ value: string, zoom: string } | undefined>();
  const [selectedLatitude, setSelectedLatitude] = useState<string | undefined>();
  const [selectedLongitude, setSelectedLongitude] = useState<string | undefined>();
  const [selectedStreet, setSelectedStreet] = useState<string>("");
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [selectedZipCode, setSelectedZipCode] = useState<string>("");
  const [selectedRegisteredAfterDate, setSelectedRegisteredAfterDate] = useState<Date | null>(null);
  const [selectedRegisteredBeforeDate, setSelectedRegisteredBeforeDate] = useState<Date | null>(null);
  const [dateRangeError, setDateRangeError] = useState<boolean>(false);
  const [registeredAfterDateError, setRegisteredAfterDateError] = useState<boolean>(false);
  const [registeredBeforeDateError, setRegisteredBeforeDateError] = useState<boolean>(false);
  const [platformStatistics, setPlatformStatistics] = useState<IPlatformStatistics | undefined>();

  const [selectedVINDescription, setSelectedVINDescription] = useState<string>('');
  const [selectedVINFileName, setSelectedVINFileName] = useState<string>(data?.vinTargetsOriginalFileName ? data.vinTargetsOriginalFileName : '');
  const [selectedVINFileGUID, setSelectedVINFileGUID] = useState<string>(data?.vinTargetsClaimCheckIdentifier ? data.vinTargetsClaimCheckIdentifier : '');

  const [bypassUserNotificationOptOut, setBypassUserNotificationOptOut] = useState<boolean>(data?.bypassUserNotificationOptOut ? data.bypassUserNotificationOptOut : false);

  const [selectedTab, setSelectedTab] = useState((!!data?.vinTargetsClaimCheckIdentifier) ? 1 : 0);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (data?.id) {
      const itemService = new APIService();
      itemService.retrieveStatistics(data.id).then((statistics: void | ICampaignStatistics) => {
        if (statistics) {

          const platformStatistics: IPlatformStatistics = {
            iOS: { sent: 0, errored: 0, received: 0, opened: 0, deleted: 0 },
            Android: { sent: 0, errored: 0, received: 0, opened: 0, deleted: 0 }
          }

          // Begin mapping the CampaignStatistics to PlatformStatistics.
          if (statistics) {
            let platform: IStatistics | undefined;

            // Process Reply stats
            statistics.appReplyStatusCounts.forEach((statisticsReply: IStatisticsReply) => {
              switch (statisticsReply.statusCountTypeId) {
                case StatusTypeId.iOS:
                  platform = platformStatistics.iOS;
                  break;
                case StatusTypeId.Android:
                  platform = platformStatistics.Android;
                  break;
                default: break;
              }

              if (platform) {
                switch (statisticsReply.appReplyTypeId) {
                  case AppReplyTypeId.AppNotifyReceived:
                    platform.received += statisticsReply.count;
                    break;
                  case AppReplyTypeId.AppNotifyRead:
                    platform.opened += statisticsReply.count;
                    break;
                  case AppReplyTypeId.AppNotifyDeleted:
                    platform.deleted += statisticsReply.count;
                    break;
                  default: break;
                }
              }
            });

            // Process Notification stats
            statistics.notificationStatusCounts.forEach((notifications: IStatisticsNotification) => {
              platform = undefined;
              switch (notifications.statusCountTypeId) {
                case StatusTypeId.iOS:
                  platform = platformStatistics.iOS;
                  break;
                case StatusTypeId.Android:
                  platform = platformStatistics.Android;
                  break;
                default: break;
              }

              if (platform) {
                platform.sent += notifications.success;
                platform.errored += notifications.error;
              }
            })
          }
          // Done mapping the CampaignStatistics to PlatformStatistics.

          setPlatformStatistics(platformStatistics);
        }
      })
        .catch((reason: any) => {
          console.log(reason);
        })
    }
  }, [data, setPlatformStatistics]);

  const cancel = () => {
    handleCancel();
  }

  const handleCancel = () => {
    setIsViewPaneOpen(false);
  }

  const isNumeric = (n: any) => {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }

  const duplicate = () => {
    const longitude: string = (!!selectedLongitude) ? selectedLongitude : '';
    const latitude: string = (!!selectedLatitude) ? selectedLatitude : '';
    const distance: string = (!!selectedDistance) ? selectedDistance.value : '';

    const campaign: ICampaign = {
      makes: selectedMakes,
      models: selectedModels,
      years: selectedYears,
      states: selectedUSStates,
      geoFenceState: selectedUSState,
      geoFenceCity: selectedCity,
      zipCodes: selectedZipCodes,
      geoFenceZipCode: selectedZipCode,
      geoFenceStreetAddress: selectedStreet,
      geoFenceRadiusInMiles: isNumeric(distance) ? Number.parseFloat(distance) : 0,
      geoFenceLatitude: isNumeric(latitude) ? Number.parseFloat(latitude) : 0,
      geoFenceLongitude: isNumeric(longitude) ? Number.parseFloat(longitude) : 0,
      registeredAfterDate: !selectedRegisteredAfterDate ? undefined : selectedRegisteredAfterDate.toISOString(),
      registeredBeforeDate: !selectedRegisteredBeforeDate ? undefined : selectedRegisteredBeforeDate.toISOString(),
      title: data ? data.title : "",
      messageBody: data ? data.messageBody : "",
      url: data ? data.url : "",
      createdDateTime: "",
      submittedDateTime: "",
      scheduledDateTime: "",
      submittingUser: undefined,
      id: undefined,

      vinTargetsOriginalFileName: data ? data.vinTargetsOriginalFileName : "",
      vinTargetsClaimCheckIdentifier: data ? data.vinTargetsClaimCheckIdentifier : "",
      vinTargetsDescription: data ? data.vinTargetsDescription : "",
      bypassUserNotificationOptOut: data ? data.bypassUserNotificationOptOut : true
    }

    handleDuplicate(campaign);
  }

  return (
    <Fragment>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', paddingTop: 0 }} >
        <Tabs value={selectedTab} onChange={handleChangeTab} aria-label="Select Target Criteria">
          <Tab label="Detailed Selection Criteria" {...a11yProps(0)} disabled={true} />
          <Tab label='VIN'  {...a11yProps(1)} disabled={true} />
        </Tabs>
      </Box>

      <TabPanel value={selectedTab} index={0}>
        <div>
          <SelectMakes initialValue={data?.makes}
            selectedMakes={selectedMakes}
            setSelectedMakes={setSelectedMakes}
            onChangeMakes={() => { }}
            readOnly={true} />
          <SelectModels initialValue={data?.models}
            selectedMakes={selectedMakes}
            selectedModels={selectedModels}
            setSelectedModels={setSelectedModels}
            onChangeModels={() => { }}
            readOnly={true} />
          <SelectYears initialValue={data?.years}
            selectedMakes={selectedMakes}
            selectedModels={selectedModels}
            selectedYears={selectedYears}
            setSelectedYears={setSelectedYears}
            onChangeYears={() => { }}
            readOnly={true} />
          <BillingAddress data={data} readOnly={true} setError={() => { }} setIsDirty={() => { }}
            setSelectedUSStates={setSelectedUSStates} selectedUSStates={selectedUSStates}
            setSelectedZipCodes={setSelectedZipCodes} selectedZipCodes={selectedZipCodes} />
          <GeoFence data={data} hasFetchGeoButton={false} readOnly={true} setError={() => { }} setIsDirty={() => { }} setHasEmptyRequiredFields={() => { }}
            selectedStreet={selectedStreet} setSelectedStreet={setSelectedStreet}
            selectedCity={selectedCity} setSelectedCity={setSelectedCity}
            selectedUSState={selectedUSState} setSelectedUSState={setSelectedUSState}
            selectedZipCode={selectedZipCode} setSelectedZipCode={setSelectedZipCode}
            selectedLatitude={selectedLatitude} setSelectedLatitude={setSelectedLatitude}
            selectedLongitude={selectedLongitude} setSelectedLongitude={setSelectedLongitude}
            selectedDistance={selectedDistance} setSelectedDistance={setSelectedDistance} />
          <SelectDateRange initialValue={{ registeredAfterDate: data?.registeredAfterDate, registeredBeforeDate: data?.registeredBeforeDate }}
            selectedRegisteredAfterDate={selectedRegisteredAfterDate}
            selectedRegisteredBeforeDate={selectedRegisteredBeforeDate}
            setSelectedRegisteredAfterDate={setSelectedRegisteredAfterDate}
            setSelectedRegisteredBeforeDate={setSelectedRegisteredBeforeDate}
            dateRangeError={dateRangeError}
            setDateRangeError={setDateRangeError}
            registeredAfterDateError={registeredAfterDateError}
            setRegisteredAfterDateError={setRegisteredAfterDateError}
            registeredBeforeDateError={registeredBeforeDateError}
            setRegisteredBeforeDateError={setRegisteredBeforeDateError}
            readOnly={true} />
        </div>
      </TabPanel>
      <TabPanel value={selectedTab} index={1}>
        <div>
          <SelectVINs
            initialVINFileName={data?.vinTargetsOriginalFileName}
            selectedVINFileName={selectedVINFileName}
            setSelectedVINFileName={setSelectedVINFileName}
            initialVINDescription={data?.vinTargetsDescription}
            selectedVINDescription={selectedVINDescription}
            setSelectedVINDescription={setSelectedVINDescription}
            initialVINFileGUID={data?.vinTargetsClaimCheckIdentifier}
            selectedVINFileGUID={selectedVINFileGUID}
            setSelectedVINFileGUID={setSelectedVINFileGUID}
            initialBypassUserNotificationOptOut={(data?.bypassUserNotificationOptOut === undefined) ? false : data.bypassUserNotificationOptOut}
            bypassUserNotificationOptOut={bypassUserNotificationOptOut}
            setBypassUserNotificationOptOut={setBypassUserNotificationOptOut}
            setListOfWMIs={() => {}}
            listOfWMIs={''}
            onChange={() => { }}
            readOnly={true}
          />
        </div>
      </TabPanel>
      <ViewTabSection data={data} platformStatistics={platformStatistics ? platformStatistics : undefined} />

      <div className={cx('button-div')}>
        <span className={cx('cancel-button')}>
          <Button variant="contained" onClick={cancel}>Close</Button>
        </span>
        <span className={cx('duplicate-button')}>
          <Button variant="contained" onClick={duplicate}>Duplicate</Button>
        </span>
      </div>
      <br />
    </Fragment>
  );
};
